'use client'

import type { VariantProps } from 'class-variance-authority'

import { X } from 'lucide-react'
import * as React from 'react'

import { cn } from '../../utils/cn'
import { Alert, type alertVariants } from './Alert'
import { Button } from './Button'

function readDismissed(key: string): boolean {
  if (typeof localStorage === 'undefined' || typeof localStorage.getItem !== 'function')
    return false
  try {
    return localStorage.getItem(key) === 'true'
  } catch {
    return false
  }
}

function writeDismissed(key: string): void {
  if (typeof localStorage === 'undefined' || typeof localStorage.setItem !== 'function') return
  try {
    localStorage.setItem(key, 'true')
  } catch {
    // ignore quota / security errors
  }
}

interface DismissibleAlertProps
  extends Omit<React.ComponentProps<'div'>, 'key'>, VariantProps<typeof alertVariants> {
  /**
   * When provided, dismissal state is persisted in `localStorage` under this key so the alert
   * stays dismissed across reloads. When omitted, dismissal is session-only (component state).
   */
  storageKey?: string
  /**
   * Icon rendered to the left of the alert content. See `Alert` for default behavior — the
   * variant-specific icon is used when this prop is omitted.
   */
  icon?: React.ReactNode | null | false
  /**
   * When false the alert is always visible and no dismiss button is shown.
   * Defaults to true.
   */
  dismissible?: boolean
}

function DismissibleAlert({
  storageKey,
  variant,
  className,
  icon,
  dismissible = true,
  children,
  ...props
}: DismissibleAlertProps) {
  const [dismissed, setDismissed] = React.useState<boolean | null>(null)

  React.useEffect(() => {
    if (!dismissible) {
      setDismissed(false)
      return
    }
    setDismissed(storageKey ? readDismissed(storageKey) : false)
  }, [storageKey, dismissible])

  if (dismissed !== false) return null

  function handleDismiss() {
    if (storageKey) writeDismissed(storageKey)
    setDismissed(true)
  }

  return (
    <Alert
      variant={variant}
      icon={icon}
      className={cn('relative', dismissible && 'pr-10', className)}
      {...props}
    >
      {children}
      {dismissible && (
        <Button
          variant="ghost"
          size="iconSm"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="absolute top-2 right-2 opacity-70 hover:opacity-100"
        >
          <X />
        </Button>
      )}
    </Alert>
  )
}

export { DismissibleAlert }
